import { Column } from 'further-ui/components/Layout';
import { FC } from 'react';
import FormField from 'further-ui/components/FormField';
import FileUpload from 'components/new/FileUpload';
import { UploadType } from 'further-types/files';
import { Control, Controller } from 'react-hook-form';
import type { TrancheFormInput } from '../../useTrancheForm';
import { TrancheDotColor } from '../dotColors';

type Props = {
  control: Control<TrancheFormInput>;
  isSyndicate: boolean;
  isLP: boolean;
  showDraftDots: boolean;
};

const SubscriptionDocumentation: FC<Props> = ({
  control,
  isSyndicate,
  isLP,
  showDraftDots,
}) => {
  if (isSyndicate) {
    return (
      <Column spacing="lg" fullWidth>
        <FormField size="md">
          <Controller
            name="additionalDocuments"
            control={control}
            render={({ field }) => (
              <FileUpload
                inputTitle="Deal documentation"
                inputTitleTooltip="Please upload all documentation relating to this opportunity that you want investors to acknowledge or agree to."
                uploadType={UploadType.FundDocuments}
                files={field.value}
                maxFiles={10}
                onChange={(files) => field.onChange(files)}
                onDeleteFile={(index) =>
                  field.onChange(field.value.filter((_, i) => i !== index))
                }
              />
            )}
          />
        </FormField>
      </Column>
    );
  }

  return (
    <Column spacing="lg" fullWidth>
      <FormField size="md">
        <Controller
          name="applicationFormFile"
          control={control}
          render={({ field, fieldState }) => (
            <FileUpload
              maxFiles={1}
              inputTitle="Legal agreement"
              inputTitleTooltip="Please upload your subscription management agreement, limited partnership agreement or, if you have a prospectus, your application form."
              inputTitleDotColor={
                showDraftDots ? TrancheDotColor.Draft : undefined
              }
              uploadType={UploadType.FundDocuments}
              files={field.value ? [field.value] : []}
              onChange={(files) =>
                field.onChange(files[files.length - 1] ?? null)
              }
              onDeleteFile={() => field.onChange(null)}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </FormField>
      {!isLP && (
        <>
          <FormField size="md">
            <Controller
              name="prospectusFile"
              control={control}
              render={({ field, fieldState }) => (
                <FileUpload
                  inputTitle="Offering document"
                  inputTitleTooltip="Please upload your core fund marketing document. This may be a prospectus, information memorandum or key fundraising presentation."
                  uploadType={UploadType.FundDocuments}
                  files={field.value ? [field.value] : []}
                  onChange={(files) =>
                    field.onChange(files[files.length - 1] ?? null)
                  }
                  onDeleteFile={() => field.onChange(null)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </FormField>
          <FormField size="md">
            <Controller
              name="keyInfoFile"
              control={control}
              render={({ field, fieldState }) => (
                <FileUpload
                  inputTitle="Key information document"
                  inputTitleTooltip="Please upload your Key Information Document (KID) if you have been required to produce one."
                  uploadType={UploadType.FundDocuments}
                  files={field.value ? [field.value] : []}
                  onChange={(files) =>
                    field.onChange(files[files.length - 1] ?? null)
                  }
                  onDeleteFile={() => field.onChange(null)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </FormField>
        </>
      )}
    </Column>
  );
};

export default SubscriptionDocumentation;
